import { cloneElement, type JSX } from "react";

import type { ProgressRootProps as ArkProgressRootProps } from "@ark-ui/react/progress";
import { Progress as ArkProgress, useProgress } from "@ark-ui/react/progress";
import {
  cx,
  type RecipeVariantProps,
  sva,
} from "@mobsuccess-devops/styled-system/css";
import { circle, hstack } from "@mobsuccess-devops/styled-system/patterns";

import { composition } from "../../../features/utils/composition";

import { ProgressValueText } from "./ProgressValueText";

export type CircularProgressProps = Omit<
  ArkProgressRootProps,
  "asChild" | "value"
> &
  RecipeVariantProps<typeof styles> & {
    value?: ArkProgressRootProps["value"] | "indeterminate";
  };

export function CircularProgress({
  size,
  palette,
  variant,
  children,
  className,
  hidden,
  ...props
}: CircularProgressProps): JSX.Element {
  const classes = styles({ palette, size, variant });

  const machine = useProgress({
    ...props,
    value: props.value === "indeterminate" ? null : props.value,
  });

  const { label, valueText } = composition.split(children, {
    label: composition.single(ArkProgress.Label),
    valueText: composition.single(ProgressValueText),
  });

  return (
    <ArkProgress.RootProvider
      value={machine}
      hidden={hidden}
      className={cx(classes.root, className)}
    >
      {label}
      <ArkProgress.Circle className={classes.circle}>
        <ArkProgress.CircleTrack className={classes.track} />
        <ArkProgress.CircleRange className={classes.range} />
        {valueText &&
          cloneElement(valueText, {
            _type: "circular",
            className: classes.valueText,
          })}
      </ArkProgress.Circle>
    </ArkProgress.RootProvider>
  );
}

const styles = sva({
  slots: ["root", "label", "valueText", "track", "range", "circle"],
  base: {
    root: hstack.raw(),
    track: circle.raw({
      stroke: "interface.300",
    }),
    circle: {
      "--size": "40px",
      "--thickness": "2px",
    },
    range: circle.raw({
      stroke: "colorPalette.400",
      "&[data-state='indeterminate']": {
        animation: "circle-range 1.4s linear infinite",
      },
    }),
    valueText: {
      textStyle: "muted",
      fill: "colorPalette.400",
      fontSize: "9px",
      fontWeight: 500,
      letterSpacing: "-0.01em",
    },
  },
  variants: {
    palette: {
      primary: {
        root: { colorPalette: "primary" },
      },
      secondary: { root: { colorPalette: "secondary" } },
      interface: {
        root: { colorPalette: "interface" },
        track: { stroke: "white" },
      },
      success: { root: { colorPalette: "success" } },
      danger: { root: { colorPalette: "danger" } },
      warning: { root: { colorPalette: "warning" } },
      info: { root: { colorPalette: "info" } },
    },
    size: {
      xs: {
        circle: {
          "--size": "12px",
        },
      },
      sm: {
        circle: {
          "--size": "24px",
        },
      },
      md: {
        circle: {
          "--size": "32px",
        },
      },
      lg: {
        circle: {
          "--size": "48px",
          "--thickness": "4px",
        },
      },
    },
    variant: {
      filled: {
        track: {
          fill: "white!",
        },
      },
      outlined: {},
    },
  },
  defaultVariants: {
    size: "md",
    variant: "outlined",
    palette: "interface",
  },
});
